// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: view state of the desktop Tasks workspace that isn't data. Each place
// (a list, folder, space, My work, Everything) keeps its own Mine or Everyone,
// grouping, sort, Closed, search and filters on this device, so going away and
// coming back finds it as it was left. A place not set up yet starts from the
// latest Mine / sort / grouping / Closed choices, with no search or filters.
// Folded groups and the shown columns in order persist too; groups opened past
// their first rows, the tree search and the task open in the side panel (or the
// Fields panel) last for the session. Desktop and phone keep separate settings.

import {
	type ColumnKey,
	DEFAULT_COLUMNS,
	isColumnKey,
} from '@app/features/konvo_tasks/components/workspace/desktop/WorkspaceColumns';
import type {
	WorkspaceGroupBy,
	WorkspaceSortBy,
} from '@app/features/konvo_tasks/components/workspace/desktop/WorkspaceGrouping';
import type {WorkspaceLocation} from '@app/features/konvo_tasks/state/WorkspaceLocation';
import {getProtectedLocalStorage} from '@app/features/platform/state/ProtectedWebStorage';
import {makeAutoObservable, observable} from 'mobx';

const KEY = 'konvo:tasks:desktop-ui:v1';
/** Places remembered; the ones visited longest ago drop off first. */
const MAX_PLACES = 200;

export interface PlaceSettings {
	mine: boolean;
	showClosed: boolean;
	/** Unset: the latest grouping chosen for this kind of place. */
	groupBy?: WorkspaceGroupBy;
	sortBy: WorkspaceSortBy;
	query: string;
	hiddenStatuses: Array<string>;
	hiddenPriorities: Array<number>;
	assignees: Array<string>;
	/** Last changed, to drop the oldest places. */
	at: number;
}

interface Persisted {
	/** The latest choices, which a place not set up yet starts from. */
	mine?: boolean;
	showClosed?: boolean;
	groupBy?: Record<string, WorkspaceGroupBy>;
	sortBy?: WorkspaceSortBy;
	collapsed?: Array<string>;
	/** Shown columns, in order. */
	columns?: Array<ColumnKey>;
	/** Before the Fields panel: default columns turned off. Read once to carry them over. */
	hiddenColumns?: Array<string>;
	places?: Record<string, PlaceSettings>;
}

const GROUPS = new Set<string>(['status', 'due', 'list', 'priority', 'assignee']);
const SORTS = new Set<string>(['due', 'priority', 'name', 'created', 'updated']);

function strings(items: unknown): Array<string> {
	return Array.isArray(items) ? items.filter((item): item is string => typeof item === 'string') : [];
}

function priorities(items: unknown): Array<number> {
	return Array.isArray(items)
		? items.filter((id: unknown): id is number => typeof id === 'number' && [0, 1, 2, 3, 4].includes(id))
		: [];
}

function readPlace(value: unknown): PlaceSettings | null {
	if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
	const place = value as Record<string, unknown>;
	return {
		mine: place.mine === true,
		showClosed: place.showClosed === true,
		groupBy: GROUPS.has(place.groupBy as string) ? (place.groupBy as WorkspaceGroupBy) : undefined,
		sortBy: SORTS.has(place.sortBy as string) ? (place.sortBy as WorkspaceSortBy) : 'due',
		query: typeof place.query === 'string' ? place.query : '',
		hiddenStatuses: strings(place.hiddenStatuses),
		hiddenPriorities: priorities(place.hiddenPriorities),
		assignees: strings(place.assignees),
		at: typeof place.at === 'number' ? place.at : 0,
	};
}

function read(): Persisted {
	try {
		const raw = getProtectedLocalStorage()?.getItem(KEY);
		const value = raw ? JSON.parse(raw) : null;
		if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
		const places: Record<string, PlaceSettings> = {};
		if (value.places && typeof value.places === 'object' && !Array.isArray(value.places)) {
			for (const [key, entry] of Object.entries(value.places)) {
				const place = readPlace(entry);
				if (place) places[key] = place;
			}
		}
		return {
			mine: value.mine === true,
			showClosed: value.showClosed === true,
			groupBy: Object.fromEntries(
				Object.entries(value.groupBy ?? {}).filter(
					([scope, group]) => (scope === 'personal' || scope === 'container') && GROUPS.has(group as string),
				),
			) as Record<string, WorkspaceGroupBy>,
			sortBy: SORTS.has(value.sortBy) ? value.sortBy : 'due',
			collapsed: strings(value.collapsed),
			columns: Array.isArray(value.columns)
				? [...new Set((value.columns as Array<unknown>).filter(isColumnKey))]
				: DEFAULT_COLUMNS.filter((column) => !strings(value.hiddenColumns).includes(column)),
			places,
		};
	} catch {
		return {};
	}
}

/** "Personal" places (my tasks) group by due date; containers and Everything by status. */
export function groupScope(location: WorkspaceLocation): 'personal' | 'container' {
	return location.kind === 'mine' || location.kind === 'today' || location.kind === 'home' ? 'personal' : 'container';
}

export function locationKey(location: WorkspaceLocation): string {
	return `${location.kind}:${location.id ?? ''}`;
}

/** A place's search and client filters, as the list filtering takes them. */
export interface PlaceFilters {
	query: string;
	hiddenStatuses: ReadonlySet<string>;
	hiddenPriorities: ReadonlySet<number>;
	assignees: ReadonlySet<string>;
}

export class DesktopWorkspaceUiStore {
	/** The latest choices, which a place not set up yet starts from. */
	mine = false;
	showClosed = false;
	groupByScope: Record<string, WorkspaceGroupBy> = {};
	sortBy: WorkspaceSortBy = 'due';
	places = observable.map<string, PlaceSettings>();
	collapsed = observable.set<string>();
	/** Shown columns, in order; field columns only appear where the tasks have the field. */
	columns: Array<ColumnKey> = [...DEFAULT_COLUMNS];
	fieldsPanelOpen = false;
	/** Groups showing all their rows ("Show N more" pressed), per location + group. */
	expandedMore = observable.set<string>();
	/** Closed groups the user unfolded while the Closed chip is off. */
	closedOpen = observable.set<string>();
	treeQuery = '';
	openTaskId: string | null = null;

	constructor() {
		const stored = read();
		this.mine = stored.mine === true;
		this.showClosed = stored.showClosed === true;
		this.groupByScope = stored.groupBy ?? {};
		this.sortBy = stored.sortBy ?? 'due';
		for (const [key, place] of Object.entries(stored.places ?? {})) this.places.set(key, place);
		for (const key of stored.collapsed ?? []) this.collapsed.add(key);
		this.columns = stored.columns ?? [...DEFAULT_COLUMNS];
		makeAutoObservable(this, {}, {autoBind: true});
	}

	private persist(): void {
		try {
			const places = [...this.places.entries()].sort((a, b) => b[1].at - a[1].at).slice(0, MAX_PLACES);
			const value: Persisted = {
				mine: this.mine,
				showClosed: this.showClosed,
				groupBy: this.groupByScope,
				sortBy: this.sortBy,
				collapsed: [...this.collapsed].slice(-300),
				columns: [...this.columns],
				places: Object.fromEntries(places),
			};
			getProtectedLocalStorage()?.setItem(KEY, JSON.stringify(value));
		} catch {}
	}

	/** The place's own settings, or what it would start from. */
	placeFor(location: WorkspaceLocation): PlaceSettings {
		return (
			this.places.get(locationKey(location)) ?? {
				mine: this.mine,
				showClosed: this.showClosed,
				sortBy: this.sortBy,
				query: '',
				hiddenStatuses: [],
				hiddenPriorities: [],
				assignees: [],
				at: 0,
			}
		);
	}

	private update(location: WorkspaceLocation, patch: Partial<PlaceSettings>): void {
		const current = this.placeFor(location);
		const groupBy = current.groupBy ?? this.groupByScope[groupScope(location)];
		this.places.set(locationKey(location), {...current, groupBy, ...patch, at: Date.now()});
		if (patch.mine !== undefined) this.mine = patch.mine;
		if (patch.showClosed !== undefined) this.showClosed = patch.showClosed;
		if (patch.sortBy !== undefined) this.sortBy = patch.sortBy;
		if (patch.groupBy !== undefined) this.groupByScope = {...this.groupByScope, [groupScope(location)]: patch.groupBy};
		this.persist();
	}

	mineFor(location: WorkspaceLocation): boolean {
		return this.placeFor(location).mine;
	}

	showClosedFor(location: WorkspaceLocation): boolean {
		return this.placeFor(location).showClosed;
	}

	sortByFor(location: WorkspaceLocation): WorkspaceSortBy {
		return this.placeFor(location).sortBy;
	}

	queryFor(location: WorkspaceLocation): string {
		return this.placeFor(location).query;
	}

	filtersFor(location: WorkspaceLocation): PlaceFilters {
		const place = this.placeFor(location);
		return {
			query: place.query,
			hiddenStatuses: new Set(place.hiddenStatuses),
			hiddenPriorities: new Set(place.hiddenPriorities),
			assignees: new Set(place.assignees),
		};
	}

	/** Status and priority filters on (the Filter chip's count). */
	filterCountFor(location: WorkspaceLocation): number {
		const place = this.placeFor(location);
		return place.hiddenStatuses.length + place.hiddenPriorities.length;
	}

	/** Search, filters or assignees narrowing the place. */
	isFiltered(location: WorkspaceLocation): boolean {
		const place = this.placeFor(location);
		return Boolean(place.query.trim()) || this.filterCountFor(location) > 0 || place.assignees.length > 0;
	}

	groupByFor(location: WorkspaceLocation): WorkspaceGroupBy {
		const scope = groupScope(location);
		const stored = this.placeFor(location).groupBy ?? this.groupByScope[scope];
		if (stored === 'list' && location.kind === 'list') return 'status';
		return stored ?? (scope === 'personal' ? 'due' : 'status');
	}

	setGroupBy(location: WorkspaceLocation, value: WorkspaceGroupBy): void {
		this.update(location, {groupBy: value});
	}

	setSortBy(location: WorkspaceLocation, value: WorkspaceSortBy): void {
		this.update(location, {sortBy: value});
	}

	setMine(location: WorkspaceLocation, value: boolean): void {
		this.update(location, {mine: value});
	}

	setShowClosed(location: WorkspaceLocation, value: boolean): void {
		this.update(location, {showClosed: value});
	}

	isCollapsed(location: WorkspaceLocation, groupKey: string): boolean {
		return this.collapsed.has(`${locationKey(location)}|${groupKey}`);
	}

	toggleCollapsed(location: WorkspaceLocation, groupKey: string): void {
		const key = `${locationKey(location)}|${groupKey}`;
		if (this.collapsed.has(key)) this.collapsed.delete(key);
		else this.collapsed.add(key);
		this.persist();
	}

	isExpandedMore(location: WorkspaceLocation, groupKey: string): boolean {
		return this.expandedMore.has(`${locationKey(location)}|${groupKey}`);
	}

	expandMore(location: WorkspaceLocation, groupKey: string): void {
		this.expandedMore.add(`${locationKey(location)}|${groupKey}`);
	}

	isClosedOpen(location: WorkspaceLocation): boolean {
		return this.closedOpen.has(locationKey(location));
	}

	setClosedOpen(location: WorkspaceLocation, open: boolean): void {
		if (open) this.closedOpen.add(locationKey(location));
		else this.closedOpen.delete(locationKey(location));
	}

	isColumnVisible(column: ColumnKey): boolean {
		return this.columns.includes(column);
	}

	/** Shows a column at the end, or hides it. */
	toggleColumn(column: ColumnKey): void {
		this.setColumnVisible(column, !this.isColumnVisible(column));
	}

	setColumnVisible(column: ColumnKey, visible: boolean): void {
		const rest = this.columns.filter((entry) => entry !== column);
		this.columns = visible ? [...rest, column] : rest;
		this.persist();
	}

	/** Moves a shown column to sit before `before` (or last when null). */
	moveColumn(column: ColumnKey, before: ColumnKey | null): void {
		if (column === before || !this.columns.includes(column)) return;
		const rest = this.columns.filter((entry) => entry !== column);
		const at = before ? rest.indexOf(before) : -1;
		rest.splice(at < 0 ? rest.length : at, 0, column);
		this.columns = rest;
		this.persist();
	}

	resetColumns(): void {
		this.columns = [...DEFAULT_COLUMNS];
		this.persist();
	}

	setFieldsPanelOpen(open: boolean): void {
		this.fieldsPanelOpen = open;
		if (open) this.openTaskId = null;
	}

	setQuery(location: WorkspaceLocation, value: string): void {
		this.update(location, {query: value});
	}

	setTreeQuery(value: string): void {
		this.treeQuery = value;
	}

	toggleHiddenStatus(location: WorkspaceLocation, key: string): void {
		this.update(location, {hiddenStatuses: toggled(this.placeFor(location).hiddenStatuses, key)});
	}

	toggleHiddenPriority(location: WorkspaceLocation, id: number): void {
		this.update(location, {hiddenPriorities: toggled(this.placeFor(location).hiddenPriorities, id)});
	}

	toggleAssignee(location: WorkspaceLocation, id: string): void {
		this.update(location, {assignees: toggled(this.placeFor(location).assignees, id)});
	}

	clearStatusAndPriorityFilters(location: WorkspaceLocation): void {
		this.update(location, {hiddenStatuses: [], hiddenPriorities: []});
	}

	clearAssignees(location: WorkspaceLocation): void {
		this.update(location, {assignees: []});
	}

	/** Search, filters and assignees off. */
	clearFilters(location: WorkspaceLocation): void {
		this.update(location, {query: '', hiddenStatuses: [], hiddenPriorities: [], assignees: []});
	}

	openTask(taskId: string | null): void {
		this.openTaskId = taskId;
		if (taskId) this.fieldsPanelOpen = false;
	}

	/** A task to open once the page has moved to its list (the move closes the panel first). */
	private queued: {taskId: string; at: number} | null = null;

	queueOpenTask(taskId: string): void {
		this.queued = {taskId, at: Date.now()};
		this.openTaskId = taskId;
	}

	/** On a location change: a task queued just now, else nothing (a stale one is dropped). */
	takeQueuedTask(): string | null {
		const queued = this.queued;
		this.queued = null;
		return queued && Date.now() - queued.at < 5000 ? queued.taskId : null;
	}
}

function toggled<T>(items: ReadonlyArray<T>, item: T): Array<T> {
	return items.includes(item) ? items.filter((entry) => entry !== item) : [...items, item];
}

export default new DesktopWorkspaceUiStore();
